import { useState } from "react";

import {
  formatError,
  formatHeading,
  solve,
  type CompassError,
  type Hand,
  type ReferenceFrame,
} from "@nav-games/compass";

/// Bidirectional compass-error calculator for the Reference tab.
/// Enter a known heading (compass or true) plus variation and
/// deviation; it fills in the other two frames and shows the
/// step-by-step CADET working so it teaches rather than just answers.

type Direction = "c2t" | "t2c";

const FRAME_LABEL: Record<ReferenceFrame, string> = {
  compass: "Compass",
  magnetic: "Magnetic",
  true: "True",
};

function ErrorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: CompassError;
  onChange: (e: CompassError) => void;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
      <span className="small muted">{label}</span>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="number"
          min={0}
          max={180}
          inputMode="numeric"
          value={value.degrees}
          onChange={(e) =>
            onChange({
              degrees: clampDeg(Number(e.target.value)),
              hand: value.hand,
            })
          }
          style={inputStyle}
          aria-label={`${label} degrees`}
        />
        <div style={{ display: "flex" }}>
          {(["E", "W"] as const).map((h) => (
            <button
              key={h}
              type="button"
              className={value.hand === h ? "" : "secondary"}
              onClick={() => onChange({ degrees: value.degrees, hand: h })}
              style={{
                minHeight: 0,
                padding: "8px 12px",
                borderRadius: h === "E" ? "8px 0 0 8px" : "0 8px 8px 0",
              }}
              aria-pressed={value.hand === h}
              aria-label={`${label} ${h === "E" ? "east" : "west"}`}
            >
              {h}
            </button>
          ))}
        </div>
      </div>
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function clampDeg(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(180, Math.round(n)));
}

export function CompassCalculator() {
  const [direction, setDirection] = useState<Direction>("c2t");
  const [knownStr, setKnownStr] = useState("145");
  const [variation, setVariation] = useState<CompassError>({ degrees: 5, hand: "W" });
  const [deviation, setDeviation] = useState<CompassError>({ degrees: 3, hand: "E" });

  const knownFrame: ReferenceFrame = direction === "c2t" ? "compass" : "true";
  const knownNum = Number(knownStr);
  const valid = knownStr.trim() !== "" && Number.isFinite(knownNum);
  const solution = valid ? solve(knownFrame, knownNum, variation, deviation) : null;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Compass ⇄ True calculator</h3>

      {/* Direction toggle. */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button
          type="button"
          className={direction === "c2t" ? "" : "secondary"}
          onClick={() => setDirection("c2t")}
          style={{ flex: 1 }}
          aria-pressed={direction === "c2t"}
        >
          Compass → True
        </button>
        <button
          type="button"
          className={direction === "t2c" ? "" : "secondary"}
          onClick={() => setDirection("t2c")}
          style={{ flex: 1 }}
          aria-pressed={direction === "t2c"}
        >
          True → Compass
        </button>
      </div>

      {/* Inputs. */}
      <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
        <span className="small muted">{FRAME_LABEL[knownFrame]} heading (°)</span>
        <input
          type="number"
          min={0}
          max={359}
          inputMode="numeric"
          value={knownStr}
          onChange={(e) => setKnownStr(e.target.value)}
          style={inputStyle}
          aria-label={`${FRAME_LABEL[knownFrame]} heading in degrees`}
        />
      </label>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <ErrorInput label="Variation" value={variation} onChange={setVariation} />
        <ErrorInput label="Deviation" value={deviation} onChange={setDeviation} />
      </div>

      {/* Result. */}
      {solution ? (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 8,
              marginBottom: 12,
            }}
          >
            {(["compass", "magnetic", "true"] as const).map((f) => {
              const deg =
                f === "compass"
                  ? solution.compassDeg
                  : f === "magnetic"
                    ? solution.magneticDeg
                    : solution.trueDeg;
              const isTarget =
                (direction === "c2t" && f === "true") ||
                (direction === "t2c" && f === "compass");
              return (
                <div
                  key={f}
                  style={{
                    textAlign: "center",
                    padding: "10px 4px",
                    borderRadius: "var(--radius-sm)",
                    border: `1px solid ${isTarget ? "var(--accent)" : "var(--border)"}`,
                    background: isTarget ? "var(--accent-soft)" : "transparent",
                  }}
                >
                  <div className="small muted">{FRAME_LABEL[f]}</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>
                    {formatHeading(deg)}°
                  </div>
                </div>
              );
            })}
          </div>

          {/* Working. */}
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            {solution.steps.map((s, i) => (
              <li key={i} className="small" style={{ marginBottom: 4 }}>
                {FRAME_LABEL[s.from]} {formatHeading(s.fromValue)}° {s.operation}{" "}
                {s.error.degrees}° = {FRAME_LABEL[s.to]} {formatHeading(s.toValue)}°{" "}
                <span className="muted">
                  ({s.errorName} {formatError(s.error)})
                </span>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Enter a heading to see the conversion.
        </p>
      )}
    </div>
  );
}
